import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#bindMegaCloseButton();

    this.refs.details?.addEventListener('toggle', this.#onDetailsToggle);
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
    window.addEventListener('resize', this.#syncMegaDrawerHeight);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details?.removeEventListener('toggle', this.#onDetailsToggle);
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    window.removeEventListener('resize', this.#syncMegaDrawerHeight);
    this.#setCloseHitBridge(false);
    this.#unbindMegaCloseButton();
  }

  /**
   * Theme editor overlays can sit above drawer chrome and steal clicks from
   * the close control. Bind capture-phase directly on the chrome close button.
   */
  #bindMegaCloseButton() {
    const button = this.querySelector('.drawer-mega__chrome-close');
    if (!button) return;

    if (button !== this.#megaCloseButton) {
      this.#unbindMegaCloseButton();
      this.#megaCloseButton = button;
      button.addEventListener('click', this.#onMegaCloseClick, true);
      button.addEventListener('pointerdown', this.#onMegaCloseClick, true);
    }
  }

  #unbindMegaCloseButton() {
    if (!this.#megaCloseButton) return;
    this.#megaCloseButton.removeEventListener('click', this.#onMegaCloseClick, true);
    this.#megaCloseButton.removeEventListener('pointerdown', this.#onMegaCloseClick, true);
    this.#megaCloseButton = null;
  }

  /** @type {HTMLElement | null} */
  #megaCloseButton = null;

  /** @type {boolean} */
  #closing = false;

  #onDetailsToggle = () => {
    const open = this.refs.details?.open;
    if (open && !this.refs.details.classList.contains('menu-open')) {
      // Theme editor may open <details> natively without our open() path.
      this.open();
    }
    this.#setCloseHitBridge(Boolean(open));
  };

  #onSectionLoad = () => {
    this.#megaCloseButton = null;
    this.#bindMegaCloseButton();
    this.#setCloseHitBridge(this.isOpen);
  };

  /**
   * Treat pointerdowns that land on the close control’s box as close — even when
   * a theme-editor overlay is the event target.
   * @param {boolean} active
   */
  #setCloseHitBridge(active) {
    document.removeEventListener('pointerdown', this.#onCloseHitBridge, true);
    if (active) {
      document.addEventListener('pointerdown', this.#onCloseHitBridge, true);
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #onCloseHitBridge = (event) => {
    const button = this.#megaCloseButton || this.querySelector('.drawer-mega__chrome-close');
    if (!button || button.hidden || !this.isOpen) return;
    // Let the button handle its own events when it is the real target.
    if (event.target instanceof Node && button.contains(event.target)) return;

    const rect = button.getBoundingClientRect();
    const { clientX: x, clientY: y } = event;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.close();
  };

  /**
   * @param {Event} event
   */
  #onMegaCloseClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'pointerdown') {
      event.stopImmediatePropagation();
    }
    this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer.
   * preventDefault keeps `<details>` open-state under our control so the
   * theme editor (and native summary toggle) cannot desync the X icon from
   * the `.menu-open` slide-in class.
   * Use `.menu-open` (not the open attribute) — native details can flip
   * `[open]` before our handler runs and would otherwise invert open/close.
   * @param {Event} [event]
   */
  toggle(event) {
    if (event instanceof Event) {
      event.preventDefault();
    }

    const visuallyOpen = this.refs.details.classList.contains('menu-open');
    return visuallyOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    // Must cancel the native <summary> toggle. Otherwise the browser flips `open`
    // again after we set it, leaving `.menu-open` without `[open]` — and UA styles
    // keep the submenu content unusable (clicks appear to do nothing).
    if (event instanceof Event) {
      event.preventDefault();
    }

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;
    if (details === this.refs.details) this.#closing = false;

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    // Flush layout so the closed transform is the transition's start value, then
    // reveal synchronously. Deliberately not deferred to rAF: a dropped frame
    // callback would leave the drawer open but never slid in.
    const panel = details.querySelector(':scope > .menu-drawer');
    if (panel instanceof HTMLElement) void panel.offsetWidth;

    details.classList.add('menu-open');
    this.#bindMegaCloseButton();
    this.#setCloseHitBridge(details === this.refs.details && this.isOpen);
    this.#syncMegaDrawerHeight();
    requestAnimationFrame(() => this.#syncMegaDrawerHeight());
    setTimeout(() => {
      trapFocus(details);
    }, 0);
  }

  /**
   * Sticky header `backdrop-filter` makes position:fixed relative to the header
   * section. Size the mega drawer to the remaining viewport so the footer is not clipped.
   */
  #syncMegaDrawerHeight = () => {
    const panel = this.querySelector('.menu-drawer--mega, .menu-drawer:has(.drawer-mega)');
    if (!(panel instanceof HTMLElement)) return;
    const top = panel.getBoundingClientRect().top;
    const height = Math.max(0, Math.round(window.innerHeight - top));
    panel.style.setProperty('--mega-drawer-visible-height', `${height}px`);
  };

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Whether drawer open/close should skip waiting on CSS animations.
   * @returns {boolean}
   */
  #shouldSkipDrawerMotion() {
    return Boolean(window.Shopify?.designMode);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;
    if (!details.open && !details.classList.contains('menu-open')) return;
    if (details === this.refs.details && this.#closing) return;
    if (details === this.refs.details) this.#closing = true;

    summary.setAttribute('aria-expanded', 'false');
    this.#setCloseHitBridge(false);

    const panel = details.querySelector(':scope > .menu-drawer');
    details.classList.remove('menu-open');

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (details === this.refs.details) this.#closing = false;

      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    };

    if (this.#shouldSkipDrawerMotion()) {
      finish();
      return;
    }

    // The panel slides out via a CSS transition; `details` must stay open until
    // it finishes, otherwise the drawer (and the close button in it) vanishes
    // instantly instead of sliding away.
    if (panel instanceof HTMLElement) {
      const onTransitionEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        panel.removeEventListener('transitionend', onTransitionEnd);
        finish();
      };
      panel.addEventListener('transitionend', onTransitionEnd);
    } else {
      onAnimationEnd(details, finish);
    }
    setTimeout(finish, 400);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
